/**
 * Permission slug constants.
 * The complete permission structure (groups, data, etc.) is loaded from the backend API.
 * This file only contains the slug constants for type safety and convenience.
 */

// Permission slug constants organized by module
export const PERMISSIONS = {
  ACCOUNTS: {
    USER: 'accounts:user',
    ROLE: 'accounts:role',
    USER_ROLE: 'accounts:user_role',
  },
  ORGANIZATIONS: {
    ORGANIZATION: 'organizations:organization',
    BILLING_ADDRESS: 'organizations:billing_address',
    SHIPPING_ADDRESS: 'organizations:shipping_address',
  },
  ITEMS: {
    ARTICLE: 'items:article',
    ARTICLE_EXPORT: 'items:article_export',
    ARTICLE_IMPORT: 'items:article_import',
    CATEGORY: 'items:category',
    PROPERTY: 'items:property',
    BOM_BASE: 'items:bom_base',
    BOM: 'items:bom',
    SHAREPOINT: 'items:sharepoint',
  },
  LOGISTICS: {
    STORAGE_ARTICLE: 'logistics:storage_article',
    STORAGE_POSITION: 'logistics:storage_position',
  },
  CONTRACT: {
    CONTRACT: 'contract:contract',
    BILL: 'contract:bill',
  },
  CUSTOMER: {
    CUSTOMER: 'customer:customer',
    CUSTOMER_ORDER: 'customer:customer_order',
    PRODUCTION_ORDER: 'customer:production_order',
  },
  REQUEST: {
    REQUEST: 'request:request',
    OFFER: 'request:offer',
  },
  SUPPLIER: {
    SUPPLIER: 'supplier:supplier',
    SUPPLIER_CONTACT: 'supplier:supplier_contact', // Note: This might not exist in backend, keeping for compatibility
    SUPPLY_ORDER: 'supplier:supply_order',
  },
  GUEST_REQUEST: {
    REQUEST: 'guest_request:request',
  },
  SUPPORT: {
    TICKET: 'support:ticket',
  },
  CONTACTS: {
    CONTACT: 'contacts:contact',
    RICH_CONTACT: 'contacts:rich_contact',
    CONTACT_GROUP: 'contacts:contact_group',
    ADDRESS: 'contacts:address',
  },
  CHAT: {
    CHAT: 'chat:channel', // Using channel as the main chat permission
    CHANNEL: 'chat:channel',
    CHANNEL_USER: 'chat:channel_user',
    MEMBER: 'chat:member',
    MESSAGE: 'chat:message',
  },
  GENERAL: {
    FILES: 'general:files',
    PERMISSIONS: 'general:permissions',
    UNITS: 'general:units',
  },
  AUTHENTICATION: {
    EMAIL: 'authentication:email',
  },
} as const

/**
 * Permission switch links for synchronizing related permissions.
 * Maps canonical permission slugs to arrays of linked permission slugs.
 * This is used to keep related permissions in sync when one is changed.
 */
export const PERMISSION_SWITCH_LINKS: Record<string, string[]> = {
  // Items permissions that should be synchronized
  [PERMISSIONS.ITEMS.ARTICLE]: [
    PERMISSIONS.ITEMS.ARTICLE,
    PERMISSIONS.ITEMS.CATEGORY,
    PERMISSIONS.ITEMS.PROPERTY,
  ],
}

// Type definitions for permission metadata
export interface PermissionData {
  slug: string
  label?: string
  groupLabel?: string
  groupDescription?: string
  description?: string
  dependencies?: string[]
  autoGrants?: string[]
  isVisible?: boolean
  meta?: Record<string, any>
}

export type PermissionDataMap = Record<string, PermissionData>

// Empty arrays for permission lists - these will be populated from backend data
// These are kept for backward compatibility with existing code
export const CONTACTS_PERMISSIONS: string[] = []
export const CUSTOMER_PERMISSIONS: string[] = []
export const ITEMS_PERMISSIONS: string[] = []
export const LOGISTICS_PERMISSIONS: string[] = []
export const REQUEST_PERMISSIONS: string[] = []
export const SUPPLIER_PERMISSIONS: string[] = []

// Legacy exports for backward compatibility
export const EMPLOYEE_FILTER_PERMISSIONS: string[] = []
export const EMPLOYEE_FILTER_PERMISSION_SET = new Set<string>()

// Legacy permission metadata definition map - now empty as metadata comes from backend
export const PERMISSION_DATA_DEFINITION_MAP: PermissionDataMap = {}

// Legacy function for building permission metadata fallback - now returns empty map
export function buildPermissionDataFallback(
  t: (key: string, options?: { defaultValue?: string }) => string
): PermissionDataMap {
  return {}
}
